/**
 * Small, calm, looping motion graphics for the dashboard tour — indigo→violet only, per brand (no
 * illustrations, no gradients elsewhere in the product). Ported verbatim from
 * `nivex-design/apps/nivex-web-v3/src/components/TourGraphics.tsx` (its `GoalGraphic` is
 * `TargetGraphic` here, matching this app's own `Target` nav icon for the Goals tab). Kept as
 * inline SVG + CSS animation (`dashboard-tour.css`) rather than a JSON/Lottie asset, matching the
 * design source.
 */

const STROKE_GRADIENT_ID = 'tour-gradient'

function GradientDefs() {
  return (
    <defs>
      <linearGradient id={STROKE_GRADIENT_ID} x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#444CE7" />
        <stop offset="100%" stopColor="#7C3AED" />
      </linearGradient>
    </defs>
  )
}

export function WelcomeGraphic() {
  return (
    <span className="bg-primary/10 flex size-12 shrink-0 items-center justify-center rounded-full">
      <svg width="20" height="20" viewBox="0 0 48 48" fill="none" aria-hidden="true">
        <rect x="6" y="7" width="7" height="28" rx="1.5" fill="#444CE7" />
        <polygon points="13,7 21,7 35,35 27,35" fill="#444CE7" />
        <rect x="35" y="13" width="7" height="28" rx="1.5" fill="#444CE7" />
      </svg>
    </span>
  )
}

export function PulseGraphic() {
  return (
    <svg viewBox="0 0 64 64" className="size-12" aria-hidden="true">
      <GradientDefs />
      <circle cx="32" cy="32" r="12" fill={`url(#${STROKE_GRADIENT_ID})`} />
      <circle
        cx="32"
        cy="32"
        r="12"
        fill="none"
        stroke="#7C3AED"
        strokeWidth="2"
        className="tour-pulse-ring"
      />
    </svg>
  )
}

export function BarsGraphic() {
  return (
    <svg viewBox="0 0 64 64" className="size-12" aria-hidden="true">
      <GradientDefs />
      <g fill={`url(#${STROKE_GRADIENT_ID})`}>
        <rect
          x="14"
          y="26"
          width="8"
          height="26"
          rx="2"
          className="tour-bar-grow"
          style={{ animationDelay: '0s' }}
        />
        <rect
          x="28"
          y="18"
          width="8"
          height="34"
          rx="2"
          className="tour-bar-grow"
          style={{ animationDelay: '0.2s' }}
        />
        <rect
          x="42"
          y="10"
          width="8"
          height="42"
          rx="2"
          className="tour-bar-grow"
          style={{ animationDelay: '0.4s' }}
        />
      </g>
    </svg>
  )
}

export function ChecklistGraphic() {
  return (
    <svg viewBox="0 0 64 64" className="size-12" aria-hidden="true">
      <GradientDefs />
      <rect
        x="10"
        y="8"
        width="44"
        height="48"
        rx="6"
        fill="none"
        stroke={`url(#${STROKE_GRADIENT_ID})`}
        strokeWidth="2.5"
      />
      <path d="M20 24h24M20 32h18" stroke="#A5B4FC" strokeWidth="2.5" strokeLinecap="round" />
      <path
        d="M19 42l5 5 11-12"
        fill="none"
        stroke={`url(#${STROKE_GRADIENT_ID})`}
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="tour-check-pop"
      />
    </svg>
  )
}

export function SwapGraphic() {
  return (
    <svg viewBox="0 0 64 64" className="size-12" aria-hidden="true">
      <GradientDefs />
      <g stroke={`url(#${STROKE_GRADIENT_ID})`} strokeWidth="3" strokeLinecap="round" fill="none">
        <path d="M14 24h30l-7-7" className="tour-swap-a" />
        <path d="M50 40H20l7 7" className="tour-swap-b" />
      </g>
    </svg>
  )
}

export function TargetGraphic() {
  return (
    <svg viewBox="0 0 64 64" className="size-12" aria-hidden="true">
      <GradientDefs />
      <circle
        cx="32"
        cy="32"
        r="18"
        fill="none"
        stroke={`url(#${STROKE_GRADIENT_ID})`}
        strokeWidth="2.5"
      />
      <circle
        cx="32"
        cy="32"
        r="10"
        fill="none"
        stroke={`url(#${STROKE_GRADIENT_ID})`}
        strokeWidth="2.5"
      />
      <circle
        cx="32"
        cy="32"
        r="3"
        fill={`url(#${STROKE_GRADIENT_ID})`}
        className="tour-check-pop"
      />
    </svg>
  )
}

export function HelpGraphic() {
  return (
    <svg viewBox="0 0 64 64" className="size-12" aria-hidden="true">
      <GradientDefs />
      <circle cx="32" cy="32" r="14" fill={`url(#${STROKE_GRADIENT_ID})`} />
      <circle
        cx="32"
        cy="32"
        r="14"
        fill="none"
        stroke="#7C3AED"
        strokeWidth="2"
        className="tour-pulse-ring"
      />
      <text x="32" y="38" textAnchor="middle" fontSize="16" fontWeight="700" fill="white">
        ?
      </text>
    </svg>
  )
}
